import React, { useMemo } from 'react';
import { BarChart3, MapPin, PieChart as PieChartIcon } from 'lucide-react';
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
} from 'recharts';
import { CATEGORIES } from '@/data/categories';

const catColor = (name) => CATEGORIES.find(c => c.name === name)?.color || '#D98A94';

/** Charts (top of dashboard): pie of total members + combined bar (total vs claimed) per category. */
export const ChartsDashboard = ({ stats, lang }) => {
  const { pieData, barData } = useMemo(() => {
    if (!stats?.categories) return { pieData: [], barData: [] };
    const entries = Object.entries(stats.categories)
      .filter(([, d]) => d.total > 0)
      .sort((a, b) => b[1].total - a[1].total);
    return {
      pieData: entries.map(([name, d]) => ({ name, value: d.total, color: catColor(name) })),
      barData: entries.map(([name, d]) => ({
        name: name.replace(' Match', ''),
        fullName: name,
        total: d.total,
        claimed: d.claimed || 0,
        color: catColor(name),
      })),
    };
  }, [stats]);
  if (pieData.length === 0) return null;

  return (
    <div className="mb-8 grid grid-cols-1 lg:grid-cols-5 gap-4" data-testid="charts-dashboard">
      <div className="glass-card rounded-xl p-5 lg:col-span-2">
        <div className="flex items-center gap-2 mb-4">
          <PieChartIcon className="w-5 h-5 text-[#D98A94]" />
          <h3 className="t-text font-semibold text-sm">
            {lang === 'en' ? 'Total Members per Category' : 'Total Anggota per Kategori'}
          </h3>
        </div>
        <div style={{ width: '100%', height: 300 }}>
          <ResponsiveContainer>
            <PieChart>
              <Pie
                data={pieData}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={90}
                innerRadius={45}
                paddingAngle={1}
                label={({ percent }) => percent > 0.05 ? `${(percent * 100).toFixed(0)}%` : ''}
                labelLine={false}
              >
                {pieData.map((e) => <Cell key={e.name} fill={e.color} />)}
              </Pie>
              <Tooltip formatter={(v, n) => [`${v} ${lang === 'en' ? 'members' : 'anggota'}`, n]} />
              <Legend
                verticalAlign="bottom"
                iconSize={8}
                wrapperStyle={{ fontSize: 10, lineHeight: '14px' }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="glass-card rounded-xl p-5 lg:col-span-3">
        <div className="flex items-center gap-2 mb-4">
          <BarChart3 className="w-5 h-5 text-[#C7867A]" />
          <h3 className="t-text font-semibold text-sm">
            {lang === 'en' ? 'Total vs Claimed per Category' : 'Total vs Sudah Diklaim per Kategori'}
          </h3>
        </div>
        <div style={{ width: '100%', height: 300 }}>
          <ResponsiveContainer>
            <BarChart data={barData} margin={{ top: 8, right: 8, bottom: 40, left: 0 }} barGap={0}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" vertical={false} />
              <XAxis
                dataKey="name"
                tick={{ fontSize: 10 }}
                angle={-30}
                textAnchor="end"
                interval={0}
                height={60}
              />
              <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
              <Tooltip
                cursor={{ fill: 'rgba(217,138,148,0.06)' }}
                formatter={(v, k) => [
                  v,
                  k === 'total'
                    ? (lang === 'en' ? 'Total members' : 'Total anggota')
                    : (lang === 'en' ? 'Claimed profiles' : 'Sudah diklaim'),
                ]}
                labelFormatter={(l, p) => p?.[0]?.payload?.fullName || l}
              />
              <Legend
                wrapperStyle={{ fontSize: 11, paddingTop: 4 }}
                formatter={(v) => v === 'total'
                  ? (lang === 'en' ? 'Total members' : 'Total anggota')
                  : (lang === 'en' ? 'Claimed profiles' : 'Sudah diklaim')}
              />
              <Bar dataKey="total" fill="#D98A94" radius={[6, 6, 0, 0]} name="total" />
              <Bar dataKey="claimed" fill="#22C55E" radius={[6, 6, 0, 0]} name="claimed" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

/** Recap tables (bottom of dashboard): member recap per category + top 5 cities. */
export const RecapDashboard = ({ stats, lang }) => {
  const categoryRows = useMemo(() => {
    if (!stats?.categories) return [];
    return Object.entries(stats.categories).sort((a, b) => b[1].total - a[1].total);
  }, [stats]);
  if (!stats) return null;
  const hasCategoryRecap = categoryRows.length > 0;
  const hasTopCities = stats.top_cities_overall && stats.top_cities_overall.length > 0;
  if (!hasCategoryRecap && !hasTopCities) return null;

  return (
    <div className="mt-8 space-y-6" data-testid="analytics-dashboard">
      {hasCategoryRecap && (
        <div className="glass-card rounded-xl p-5">
          <div className="flex items-center gap-2 mb-4">
            <BarChart3 className="w-5 h-5 text-[#C7867A]" />
            <h3 className="t-text font-semibold text-sm">{lang === 'en' ? 'Member Recap per Category' : 'Rekapan Member per Kategori'}</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border-color)' }}>
                  <th className="text-left py-2 px-3 t-text-muted text-xs uppercase tracking-wider">{lang === 'en' ? 'Category' : 'Kategori'}</th>
                  <th className="text-center py-2 px-3 t-text-muted text-xs uppercase tracking-wider">Total</th>
                  <th className="text-center py-2 px-3 t-text-muted text-xs uppercase tracking-wider">{lang === 'en' ? 'Male' : 'Pria'}</th>
                  <th className="text-center py-2 px-3 t-text-muted text-xs uppercase tracking-wider">{lang === 'en' ? 'Female' : 'Wanita'}</th>
                  <th className="text-center py-2 px-3 t-text-muted text-xs uppercase tracking-wider">{lang === 'en' ? 'Claimed' : 'Diklaim'}</th>
                  <th className="text-left py-2 px-3 t-text-muted text-xs uppercase tracking-wider">Top 5 {lang === 'en' ? 'Cities' : 'Kota'}</th>
                </tr>
              </thead>
              <tbody>
                {categoryRows.map(([cat, data]) => {
                    const topCities = stats.top_cities_by_category?.[cat] || [];
                    const claimPct = data.total > 0 ? Math.round(((data.claimed || 0) / data.total) * 100) : 0;
                    return (
                      <tr key={cat} style={{ borderBottom: '1px solid var(--border-color)' }} className="hover:bg-[var(--bg-input)] transition">
                        <td className="py-3 px-3">
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: catColor(cat) }} />
                            <span className="t-text font-medium text-xs whitespace-nowrap">{cat}</span>
                          </div>
                        </td>
                        <td className="text-center py-3 px-3 t-text font-bold">{data.total}</td>
                        <td className="text-center py-3 px-3">
                          <span className="inline-flex items-center gap-1 text-blue-500 text-xs font-medium">{data.male || 0}</span>
                        </td>
                        <td className="text-center py-3 px-3">
                          <span className="inline-flex items-center gap-1 text-pink-500 text-xs font-medium">{data.female || 0}</span>
                        </td>
                        <td className="text-center py-3 px-3">
                          <span className="inline-flex items-center gap-1 text-green-600 text-xs font-medium whitespace-nowrap">
                            {data.claimed || 0} <span className="t-text-muted">({claimPct}%)</span>
                          </span>
                        </td>
                        <td className="py-3 px-3">
                          <div className="flex flex-wrap gap-1">
                            {topCities.length > 0 ? topCities.map((tc) => (
                              <span key={tc.city} className="text-[10px] t-bg-input px-1.5 py-0.5 rounded-full t-text-secondary">
                                {tc.city?.split(' - ').pop()} ({tc.count})
                              </span>
                            )) : <span className="t-text-muted text-[10px]">-</span>}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
              <tfoot>
                <tr className="bg-[var(--bg-input)]">
                  <td className="py-3 px-3 t-text font-bold text-xs">TOTAL</td>
                  <td className="text-center py-3 px-3 t-text font-bold">{stats.total_profiles}</td>
                  <td className="text-center py-3 px-3 text-blue-500 font-bold text-xs">{stats.male_count}</td>
                  <td className="text-center py-3 px-3 text-pink-500 font-bold text-xs">{stats.female_count}</td>
                  <td className="text-center py-3 px-3 text-green-600 font-bold text-xs">{stats.claimed_profiles || 0}</td>
                  <td className="py-3 px-3"></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {hasTopCities && (
        <div className="glass-card rounded-xl p-5">
          <div className="flex items-center gap-2 mb-4">
            <MapPin className="w-5 h-5 text-[#D98A94]" />
            <h3 className="t-text font-semibold text-sm">{lang === 'en' ? 'Top 5 Cities Overall' : 'Top 5 Kota Keseluruhan'}</h3>
          </div>
          <div className="space-y-2">
            {stats.top_cities_overall.map((c, i) => {
              const maxCount = stats.top_cities_overall[0]?.count || 1;
              const pct = Math.round((c.count / maxCount) * 100);
              return (
                <div key={c.city} className="flex items-center gap-3">
                  <span className="w-5 text-center t-text-muted text-xs font-bold">{i + 1}</span>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="t-text text-xs font-medium">{c.city?.split(' - ').pop()}</span>
                      <span className="t-text-muted text-xs">{c.count} {lang === 'en' ? 'members' : 'anggota'}</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-[var(--bg-input)] overflow-hidden">
                      <div className="h-full rounded-full bg-[#D98A94]" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

// Legacy default export — kept so any old import site still works.
// New code should use `ChartsDashboard` (top) and `RecapDashboard` (bottom).
export const AnalyticsDashboard = RecapDashboard;
